// ===========================================================================
// ReHome — Naptárrács (hétfő az első nap)
//
// Tiszta dátumlogika, felület nélkül — így tesztelhető az a néhány szabály,
// amit könnyű elrontani, és amit ránézésre nem lehet ellenőrizni:
//
//   - a hét HÉTFŐVEL kezdődik (a JS `getDay()` vasárnappal indul),
//   - a hónapnézet mindig teljes heteket mutat, tehát a szomszédos hónapok
//     napjaival egészül ki,
//   - a "ma" összehasonlítása NAP pontosságú (az óra sose billentse át),
//   - a dátumkulcs helyi idő szerinti, nem UTC — a `toISOString()` közép-
//     európai idő szerint este 10 után már a KÖVETKEZŐ napot adná vissza,
//     és az emlékeztető rossz cellába kerülne.
// ===========================================================================

/** Hétfővel kezdődő napnevek — a fejlécekhez. */
export const WEEKDAY_LABELS_HU = ['Hétfő', 'Kedd', 'Szerda', 'Csütörtök', 'Péntek', 'Szombat', 'Vasárnap'];
export const WEEKDAY_SHORT_HU = ['H', 'K', 'Sze', 'Cs', 'P', 'Szo', 'V'];

export const MONTH_NAMES_HU = [
  'január', 'február', 'március', 'április', 'május', 'június',
  'július', 'augusztus', 'szeptember', 'október', 'november', 'december',
];

/**
 * `YYYY-MM-DD` HELYI idő szerint.
 *
 * Szándékosan nem `toISOString().split('T')[0]`: az UTC-re vált, és nyáron
 * (UTC+2) minden 22:00 utáni időpont már a másnapi kulcsot adná.
 */
export const toDateKey = (d: Date): string => {
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
};

/** `YYYY-MM-DD` -> helyi éjfélre állított Date. */
export const fromDateKey = (key: string): Date => {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};

/** A hét hányadik napja hétfőtől számolva (hétfő = 0 … vasárnap = 6). */
export const mondayIndex = (d: Date): number => (d.getDay() + 6) % 7;

/** Az adott napot tartalmazó hét hétfője, helyi éjfélkor. */
export const startOfWeek = (d: Date): Date => {
  const out = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  out.setDate(out.getDate() - mondayIndex(out));
  return out;
};

export const addDays = (d: Date, n: number): Date => {
  const out = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  out.setDate(out.getDate() + n);
  return out;
};

/**
 * Hónapléptetés a "hónap vége" csapda nélkül.
 *
 * A `setMonth` március 31-ről egy hónapot visszalépve nem februárt ad, hanem
 * március 3-át (mert február 31. nem létezik, és a Date túlcsordul). A nézet
 * ezért mindig a hónap 1. napján lépteti magát.
 */
export const addMonths = (d: Date, n: number): Date =>
  new Date(d.getFullYear(), d.getMonth() + n, 1);

export const isSameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

export interface CalendarDay {
  date: Date;
  key: string;
  /** A megjelenített hónaphoz tartozik-e (a kilógó napok halványabbak). */
  inCurrentMonth: boolean;
  isToday: boolean;
  isWeekend: boolean;
}

/**
 * A hónapnézet rácsa: teljes hetek, hétfővel kezdve.
 *
 * A hetek száma 4 és 6 között változik (egy 28 napos, hétfőn kezdődő február
 * pontosan 4 hét; egy 31 napos, vasárnap kezdődő hónap 6). A rács ezért nem
 * fixen 42 cella — a felesleges hatodik sor üresen tolná lejjebb az egész
 * naptárat.
 */
export const buildMonthGrid = (viewDate: Date, today: Date = new Date()): CalendarDay[][] => {
  const first = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
  const last = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0);

  const gridStart = startOfWeek(first);
  const gridEnd = addDays(startOfWeek(last), 6);

  const weeks: CalendarDay[][] = [];
  let cursor = gridStart;
  while (cursor <= gridEnd) {
    const week: CalendarDay[] = [];
    for (let i = 0; i < 7; i++) {
      const date = addDays(cursor, i);
      week.push({
        date,
        key: toDateKey(date),
        inCurrentMonth: date.getMonth() === viewDate.getMonth() && date.getFullYear() === viewDate.getFullYear(),
        isToday: isSameDay(date, today),
        isWeekend: mondayIndex(date) >= 5,
      });
    }
    weeks.push(week);
    cursor = addDays(cursor, 7);
  }
  return weeks;
};

/** A hétnézet hét napja, hétfőtől vasárnapig. */
export const buildWeekGrid = (viewDate: Date, today: Date = new Date()): CalendarDay[] => {
  const start = startOfWeek(viewDate);
  return Array.from({ length: 7 }, (_, i) => {
    const date = addDays(start, i);
    return {
      date,
      key: toDateKey(date),
      inCurrentMonth: date.getMonth() === viewDate.getMonth(),
      isToday: isSameDay(date, today),
      isWeekend: i >= 5,
    };
  });
};

/**
 * A rács első és utolsó napja `YYYY-MM-DD` alakban.
 *
 * A naptár EZT a tartományt kéri le a szerverről — nem az összes emlékeztetőt.
 * A hónapnézet a kilógó napokat is mutatja, tehát a tartomány szélesebb, mint
 * maga a hónap; enélkül a szomszédos hónapok látható napjai üresek lennének.
 */
export const gridRange = (days: CalendarDay[][] | CalendarDay[]): { from: string; to: string } => {
  const flat = (Array.isArray(days[0]) ? (days as CalendarDay[][]).flat() : days as CalendarDay[]);
  if (flat.length === 0) {
    const key = toDateKey(new Date());
    return { from: key, to: key };
  }
  return { from: flat[0].key, to: flat[flat.length - 1].key };
};

/** Emlékeztetők dátumkulcs szerint csoportosítva — a cellák így O(1)-ben találják meg a sajátjaikat. */
export const groupByDateKey = <T>(items: T[], getKey: (item: T) => string | null | undefined): Record<string, T[]> => {
  const out: Record<string, T[]> = {};
  for (const item of items) {
    const key = getKey(item);
    if (!key) continue;
    (out[key] ||= []).push(item);
  }
  return out;
};

/** "2026. augusztus" — a hónapnézet fejlécéhez. */
export const formatMonthTitle = (d: Date): string => `${d.getFullYear()}. ${MONTH_NAMES_HU[d.getMonth()]}`;

/**
 * "2026. augusztus 24 — 30." illetve hónapváltásnál "2026. augusztus 31 —
 * szeptember 6." — a hétnézet fejlécéhez.
 */
export const formatWeekTitle = (d: Date): string => {
  const start = startOfWeek(d);
  const end = addDays(start, 6);
  const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
  if (sameMonth) {
    return `${start.getFullYear()}. ${MONTH_NAMES_HU[start.getMonth()]} ${start.getDate()}–${end.getDate()}.`;
  }
  const sameYear = start.getFullYear() === end.getFullYear();
  const endPart = sameYear
    ? `${MONTH_NAMES_HU[end.getMonth()]} ${end.getDate()}.`
    : `${end.getFullYear()}. ${MONTH_NAMES_HU[end.getMonth()]} ${end.getDate()}.`;
  return `${start.getFullYear()}. ${MONTH_NAMES_HU[start.getMonth()]} ${start.getDate()}. – ${endPart}`;
};
